import {
  createFileRoute,
  Outlet,
  useLocation,
  useNavigate,
} from "@tanstack/react-router";
import { z } from "zod";
import { CleaningFormId, CleaningProcedureId } from "@brewbase/db/types";
import { trpc } from "@/hooks/trpc";
import { Skeleton } from "@/components/ui/skeleton";
import {
  SprayCan,
  SettingsIcon,
  ListIcon,
  FileTextIcon,
  AlertCircleIcon,
  PlusIcon,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { useSidebarConfig } from "@/hooks/use-sidebar-config";
import type { SidebarNavItem } from "@/components/nav/sidebar-store";
import { useMemo } from "react";
import { Button } from "@/components/ui/button";
import { RequireCapability } from "@/components/require-capability";

export const Route = createFileRoute(
  "/settings/cleaning-procedures/$procedure_id",
)({
  component: () => (
    <RequireCapability cap="cleaning-procedures:edit">
      <RouteComponent />
    </RequireCapability>
  ),
  params: z.object({
    procedure_id: z.coerce
      .number()
      .transform((val) => val as CleaningProcedureId),
  }),
});

function RouteComponent() {
  const { procedure_id } = Route.useParams();

  const {
    data: procedure,
    isLoading,
    error,
  } = trpc.cleaning.procedures.get.useQuery(procedure_id);

  const { data: forms } = trpc.cleaning.forms.list.useQuery({
    procedureId: procedure_id,
  });

  if (isLoading) {
    return <CleaningTypeSkeleton />;
  }

  if (error || !procedure) {
    return (
      <div className="flex items-center justify-center h-full p-8">
        <div className="text-center">
          <AlertCircleIcon className="w-10 h-10 mx-auto text-destructive mb-2" />
          <p className="text-sm text-muted-foreground">
            {error?.message ?? "Cleaning procedure not found"}
          </p>
        </div>
      </div>
    );
  }

  return (
    <CleaningProcedureContent
      procedureId={procedure_id}
      procedure={procedure}
      forms={forms ?? []}
    />
  );
}

interface CleaningProcedureContentProps {
  procedureId: CleaningProcedureId;
  procedure: {
    name: string;
    code: string;
    version: string;
    is_active: boolean;
  };
  forms: Array<{
    id: CleaningFormId;
    name: string;
    is_default: boolean;
    is_active: boolean;
  }>;
}

function CleaningProcedureContent({
  procedureId,
  procedure,
  forms,
}: CleaningProcedureContentProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const utils = trpc.useUtils();

  const pathParts = location.pathname.split("/");
  const sectionIndex = pathParts.indexOf(String(procedureId));
  const activePath =
    sectionIndex >= 0 ? (pathParts[sectionIndex + 1] ?? "") : "";
  const activeFormId =
    activePath === "forms" ? pathParts[sectionIndex + 2] : undefined;

  const createFormMutation = trpc.cleaning.forms.create.useMutation({
    onSuccess: (data) => {
      toast.success("Form created");
      utils.cleaning.forms.list.invalidate({ procedureId });
      navigate({
        to: "/settings/cleaning-procedures/$procedure_id/forms/$form_id",
        params: { procedure_id: procedureId, form_id: data.id },
      });
    },
    onError: (err) => {
      toast.error(`Failed to create form: ${err.message}`);
    },
  });

  const handleCreateForm = () => {
    const count = (forms?.length ?? 0) + 1;
    createFormMutation.mutate({
      procedureId,
      name: `Form ${count}`,
      isDefault: forms?.length === 0,
    });
  };

  const navigation = useMemo((): SidebarNavItem[] => {
    const items: SidebarNavItem[] = [
      {
        type: "link",
        id: "overview",
        label: "Overview",
        icon: SettingsIcon,
        href: `/settings/cleaning-procedures/${procedureId}`,
      },
      {
        type: "link",
        id: "fields",
        label: "Fields",
        icon: ListIcon,
        href: `/settings/cleaning-procedures/${procedureId}/fields`,
      },
    ];

    // Build forms section with children
    const formLinks: SidebarNavItem[] = forms.map((form) => ({
      type: "link" as const,
      id: `form-${form.id}`,
      label: form.name,
      href: `/settings/cleaning-procedures/${procedureId}/forms/${form.id}`,
      badge: form.is_default ? "★" : undefined,
    }));

    items.push({
      type: "section",
      id: "forms",
      label: "Forms",
      icon: FileTextIcon,
      defaultExpanded: activePath === "forms",
      children:
        formLinks.length > 0
          ? formLinks
          : [
              {
                type: "link" as const,
                id: "no-forms",
                label: "No forms yet",
                href: "#",
              },
            ],
    });

    return items;
  }, [procedureId, forms, activePath]);

  useSidebarConfig({
    layer: 2,
    parent: {
      label: "Procedures",
      icon: SprayCan,
      href: "/settings/cleaning-procedures",
    },
    header: {
      title: procedure.name,
      subtitle: `${procedure.code} • v${procedure.version}`,
      status: !procedure.is_active ? (
        <Badge variant="outline" className="text-xs">
          Inactive
        </Badge>
      ) : undefined,
    },
    navigation,
    footer: (
      <Button
        variant="outline"
        size="sm"
        className="w-full"
        onClick={handleCreateForm}
        disabled={createFormMutation.isPending}
      >
        <PlusIcon className="w-3.5 h-3.5 mr-1.5" />
        Add Form
      </Button>
    ),
  });

  return (
    <main className="flex-1 min-h-0 overflow-auto h-full">
      <Outlet />
    </main>
  );
}

function CleaningTypeSkeleton() {
  return (
    <div className="flex-1 p-4 space-y-4">
      <Skeleton className="h-8 w-48" />
      <Skeleton className="h-64 w-full" />
    </div>
  );
}
